import clsx from 'clsx';
import { format } from 'date-fns';
import { Form, Formik } from 'formik';
import { isEmpty, upperFirst } from 'lodash';
import React, { useMemo } from 'react';
import * as Yup from 'yup';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { currencyList, getMinimumChargeAmount } from '@dashboard/services/currency';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { formatPrice, formatPriceInput } from '@dashboard/services/formatting';
import { formatPriceForInput } from '@dashboard/services/formatting/price';
import getProductType, {
  getPaymentTypeLabel,
  getProductValuesFromType,
  PRODUCT_TYPES,
} from '@dashboard/services/getProductType';

import Features from '@dashboard/enums/features';
import { canUseFeature } from '@dashboard/services/features';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';

import Callout from '@dashboard/components/Callout';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import CheckboxReveal from '@dashboard/components/CheckboxReveal';
import { FormElement, Label } from '@dashboard/components/Form';
import { Checkbox, Select, TextInput } from '@dashboard/components/Formik';
import CurrencySelector from '@dashboard/components/Formik/components/CurrencySelector';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import RadioCard from '@dashboard/components/Formik/components/RadioCard';
import { InfoTooltip } from '@dashboard/components/Tooltip';

import SubscriptionPlanSelector from '../../../components/SubscriptionPlanSelector';
import { getBillingPeriod } from '../../../utils/getBillingPeriod';
import { getTrial } from '../../../utils/getTrial';
import useProduct from '@/dashboard/scenes/PageEdit/scenes/PageProduct/useProduct';

const billingPeriods = [
  { name: 'day', label: 'Daily' },
  {
    name: 'week',
    label: 'Weekly',
  },
  {
    name: 'month',
    label: 'Monthly',
  },
  {
    name: 'quarter',
    label: 'Every 3 months',
  },
  {
    name: 'semiannual',
    label: 'Every 6 months',
  },
  {
    name: 'year',
    label: 'Yearly',
  },
];

const daysOfMonth = Array.from({ length: 31 }, (_, i) => i + 1);

const months = [
  { name: '1', label: 'January' },
  { name: '2', label: 'February' },
  { name: '3', label: 'March' },
  { name: '4', label: 'April' },
  { name: '5', label: 'May' },
  { name: '6', label: 'June' },
  { name: '7', label: 'July' },
  { name: '8', label: 'August' },
  { name: '9', label: 'September' },
  { name: '10', label: 'October' },
  { name: '11', label: 'November' },
  { name: '12', label: 'December' },
];

Yup.setLocale({
  mixed: {
    required: ({ path }) => `${path} is required`,
  },
});

const productSchema = Yup.object().shape({
  title: Yup.string().min(3, '3 characters minimum').required('Product title is required'),
  type: Yup.mixed().oneOf(Object.values(PRODUCT_TYPES)).required(),
  pricingType: Yup.mixed().oneOf(['single', 'multiple']),
  currency: Yup.mixed().oneOf(Object.keys(currencyList)).required(),
  price: Yup.number().when(['type'], {
    is: type => type !== PRODUCT_TYPES.SUBSCRIPTION && type !== PRODUCT_TYPES.PAYMENT_PLAN,
    then: Yup.number().test('min', `min`, (price, values) => {
      if (!price || price === '0') {
        return true;
      }

      return (
        formatPriceInput(price, values.parent.currency) >=
        getMinimumChargeAmount(values.parent.currency)
      );
    }),
  }),
  setupFee: Yup.number()
    .nullable()
    .when(['type'], {
      is: type =>
        type === PRODUCT_TYPES.ONE_TIME ||
        type === PRODUCT_TYPES.PWYW ||
        type === PRODUCT_TYPES.SUBSCRIPTION ||
        type === PRODUCT_TYPES.PAYMENT_PLAN,
      then: Yup.number().test('min', `min`, (price, values) => {
        if (!price || price === '0') {
          return true;
        }

        return (
          formatPriceInput(price, values.parent.currency) >=
          getMinimumChargeAmount(values.parent.currency)
        );
      }),
    }),
  setupFeeMultipliesWithQuantity: Yup.boolean().nullable(),
  payWhatYouWant: Yup.boolean().nullable(),
  free: Yup.boolean().nullable(),
  billingPeriod: Yup.string().oneOf(billingPeriods.map(bp => bp.name)),
  trialPeriodDays: Yup.number()
    .nullable()
    .when(['type'], {
      is: type => type === PRODUCT_TYPES.SUBSCRIPTION || type === PRODUCT_TYPES.PAYMENT_PLAN,
      then: Yup.number().min(0, '0 days minimum').nullable(),
    }),
  billingCycleAnchorConfig: Yup.object().shape({
    enabled: Yup.boolean(),
    dayOfMonth: Yup.number().nullable(),
    month: Yup.number().nullable(),
  }),
  planIterations: Yup.number().when(['type'], {
    is: type => type === PRODUCT_TYPES.PAYMENT_PLAN,
    then: Yup.number()
      .required('No. of payments is required')
      .min(1, 'No. of payments should be empty or at least 2 payments')
      // make sure that planIterations cant go over 20 years
      .when(['billingPeriod'], {
        is: 'day',
        then: Yup.number()
          .nullable()
          .typeError('No. of payments is required')
          .max(7300, 'No. of payments should be less than 20 years'),
      })
      .when(['billingPeriod'], {
        is: 'week',
        then: Yup.number()
          .nullable()
          .typeError('No. of payments is required')
          .max(1040, 'No. of payments should be less than 20 years'),
      })
      .when(['billingPeriod'], {
        is: 'month',
        then: Yup.number()
          .nullable()
          .typeError('No. of payments is required')
          .max(240, 'No. of payments should be less than 20 years'),
      })
      .when(['billingPeriod'], {
        is: 'quarter',
        then: Yup.number()
          .nullable()
          .typeError('No. of payments is required')
          .max(80, 'No. of payments should be less than 20 years'),
      })
      .when(['billingPeriod'], {
        is: 'semiannual',
        then: Yup.number()
          .nullable()
          .typeError('No. of payments is required')
          .max(40, 'No. of payments should be less than 20 years'),
      })
      .when(['billingPeriod'], {
        is: 'year',
        then: Yup.number()
          .nullable()
          .typeError('No. of payments is required')
          .max(20, 'No. of payments should be less than 20 years'),
      }),
    otherwise: Yup.number().nullable(),
  }),
});

function getDefaultProductValues(seller) {
  return {
    type: 'charge',
    title: '',
    pricingType: 'single',
    currency: seller.defaultCurrency || 'usd',
    price: '',
    discountedFromPrice: '',
    payWhatYouWant: false,
    trial: '',
    trialPeriodDays: '',
    billingCycleAnchorConfig: {
      enabled: false,
      dayOfMonth: 1,
      month: null,
    },
    planIterations: null,
    setupFee: '',
    setupFeeMultipliesWithQuantity: false,
    startDate: null,
    media: [],
    billingPeriod: 'month',
    taxBehavior: '',
    taxCode: '',
  };
}

function getFormValues(product, seller = {}) {
  if (isEmpty(product)) {
    return getDefaultProductValues(seller);
  } else {
    return {
      type: getProductType(product),
      title: product.title,
      currency: product.currency,
      pricingType: product.pricingType || 'single',
      price: formatPriceForInput({
        amount: product.price,
        currency: product.currency,
        style: 'decimal',
      }),
      discountedFromPrice: product.discountedFromPrice
        ? formatPriceForInput({
            amount: product.discountedFromPrice,
            currency: product.currency,
            style: 'decimal',
          })
        : '',
      payWhatYouWant: product.payWhatYouWant,
      media: product.media || [],
      planId: product.planId,
      productId: product.productId,
      trial: getTrial(product),
      trialPeriodDays: product.trialPeriodDays,
      billingCycleAnchorConfig: {
        enabled: product.billingCycleAnchorConfig?.enabled,
        dayOfMonth: product.billingCycleAnchorConfig?.dayOfMonth || 1,
        month: product.billingCycleAnchorConfig?.month || null,
      },
      planIterations: product.planIterations,
      billingPeriod: getBillingPeriod(product.interval, product.intervalCount),
      setupFee: product.setupFee
        ? formatPriceForInput({
            amount: product.setupFee,
            currency: product.currency,
            style: 'decimal',
          })
        : '',
      setupFeeMultipliesWithQuantity: product.setupFeeMultipliesWithQuantity,
      startDate: product.startDate
        ? format(new Date(product.startDate), "yyyy-MM-dd'T'HH:mm")
        : null,
      planManagedInStripe: product.planManagedInStripe,
      taxBehavior: product.taxBehavior,
      taxCode: product.taxCode,
    };
  }
}

const ProductDetailsForm = ({ isNew, onChange }) => {
  const { seller } = useAuth();
  const { product, updateProduct } = useProduct();

  async function handleSubmit(formValues, actions) {
    actions.setStatus(undefined);

    try {
      const priceValues = getProductValuesFromType(formValues.type);
      const values = Object.assign({}, formValues, priceValues);

      if (values.pricingType === 'multiple' && !values.planManagedInStripe) {
        values.price = 0;
      }

      if (values.price !== 0) {
        values.price = formatPriceInput(values.price || 0, values.currency);
      }

      if (values.discountedFromPrice) {
        values.discountedFromPrice = formatPriceInput(
          values.discountedFromPrice || 0,
          values.currency,
        );
      }

      if (values.setupFee) {
        values.setupFee = formatPriceInput(values.setupFee || 0, values.currency);
      }

      if (values.startDate) {
        values.startDate = new Date(values.startDate);
      }

      if (values.billingCycleAnchorConfig.enabled) {
        if (values.billingPeriod === 'day' || values.billingPeriod === 'week') {
          values.billingCycleAnchorConfig.enabled = false;
        }
      }

      const { data, error } = await updateProduct.mutateAsync({
        ...values,
        isNew,
      });

      if (data) {
        if (onChange) {
          onChange(data);
        }

        actions.setStatus('');
        actions.resetForm({
          values: getFormValues(data),
        });
      } else {
        const { status, errors } = transformApiErrorToFormik(error);

        if (!isEmpty(errors)) {
          actions.setErrors(errors);
        } else {
          actions.setStatus(status);
        }
      }
    } catch (error) {
      if (error && typeof error.message === 'string') {
        actions.setStatus(error.message);
      } else {
        actions.setStatus('Unable to save. Please contact support');
      }
    }
  }

  const initialValues = useMemo(() => getFormValues(product, seller), [product, seller]);

  /*   const showTaxSettings =
    seller?.tax?.enabled &&
    product.type === 'subscription' &&
    !product.planManagedInStripe; */

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={productSchema}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ status, dirty, handleSubmit, values, setFieldValue }) => (
        <Form onSubmit={handleSubmit}>
          <FormElement>
            <Label htmlFor="title">Product title</Label>
            <TextInput
              name="title"
              id="title"
              type="text"
              placeholder="Product title"
              autoFocus={!!isNew}
            />
            <FormError name="title" />
          </FormElement>

          <FormElement>
            <Label>Billing model</Label>
            <BillingModelField />
          </FormElement>

          <FormElement>
            <Label>Pricing type</Label>
            <PricingTypeField />
          </FormElement>

          {values.pricingType === 'multiple' && (
            <FormElement>
              <Callout variant="info">
                <Callout.Heading>
                  {isNew ? 'Use variants to add prices later' : 'Use variants to add prices'}
                </Callout.Heading>
              </Callout>
            </FormElement>
          )}

          <>
            <FormElement>
              <div className={clsx('grid grid-cols-4 gap-2 mb-4')}>
                {values.pricingType === 'single' && (
                  <div className="col-span-1">
                    <Label htmlFor="price" disabled={values.planManagedInStripe}>
                      {values.type === PRODUCT_TYPES.PWYW ? 'Min. price' : 'Price'}

                      {values.type !== PRODUCT_TYPES.PWYW && (
                        <InfoTooltip>Add 0 to only use variant prices</InfoTooltip>
                      )}
                    </Label>
                    <TextInput
                      id="price"
                      name="price"
                      type="number"
                      step="any"
                      placeholder={values.type === PRODUCT_TYPES.PWYW ? '0.00' : '19.99'}
                      disabled={values.planManagedInStripe}
                    />
                  </div>
                )}

                <div className="col-span-1">
                  <Label htmlFor="currency" disabled={values.planManagedInStripe}>
                    Currency
                  </Label>
                  <CurrencySelector
                    id="currency"
                    name="currency"
                    disabled={values.planManagedInStripe}
                  />
                </div>

                {(values.type === PRODUCT_TYPES.SUBSCRIPTION ||
                  values.type === PRODUCT_TYPES.PAYMENT_PLAN) && (
                  <div className="col-span-2">
                    <Label htmlFor="billingPeriod" disabled={values.planManagedInStripe}>
                      Repeats
                    </Label>
                    <Select
                      name="billingPeriod"
                      id="billingPeriod"
                      disabled={values.planManagedInStripe}
                    >
                      {billingPeriods.map(billingPeriod => {
                        return (
                          <option value={billingPeriod.name} key={billingPeriod.name}>
                            {billingPeriod.label}
                          </option>
                        );
                      })}
                    </Select>
                  </div>
                )}

                {values.type === PRODUCT_TYPES.PAYMENT_PLAN && (
                  <div className="col-span-1 whitespace-nowrap">
                    <Label>
                      No. of payments{' '}
                      <InfoTooltip>
                        Sell a subscription with a fixed number of payments. The subscription
                        automatically ends after the last payment.
                      </InfoTooltip>
                    </Label>

                    <TextInput
                      name="planIterations"
                      id="planIterations"
                      type="number"
                      placeholder="4"
                    />
                  </div>
                )}
              </div>

              <div>
                {values.type !== PRODUCT_TYPES.LEAD_CAPTURE && (
                  <>
                    <FormError name="price">
                      {e => (
                        <FormErrorBase>
                          {e === 'min'
                            ? `Minimum price is ${formatPrice({
                                amount: getMinimumChargeAmount(values.currency),
                                currency: values.currency,
                              })}`
                            : e}
                        </FormErrorBase>
                      )}
                    </FormError>
                    <FormError name="currency" />
                  </>
                )}

                {(values.type === PRODUCT_TYPES.SUBSCRIPTION ||
                  values.type === PRODUCT_TYPES.PAYMENT_PLAN) && <FormError name="billingPeriod" />}

                {values.type === PRODUCT_TYPES.PAYMENT_PLAN && <FormError name="planIterations" />}
              </div>
            </FormElement>

            <div>
              {(values.type === PRODUCT_TYPES.SUBSCRIPTION ||
                values.type === PRODUCT_TYPES.PAYMENT_PLAN) && (
                <>
                  <div>
                    <FormElement>
                      <Label>
                        Trial
                        <InfoTooltip>
                          {upperFirst(getPaymentTypeLabel(values.type))} will start automatically at
                          the end of the trial, unless canceled by you or your customer.
                        </InfoTooltip>
                      </Label>
                      <TrialField setFieldValue={setFieldValue} values={values} />
                    </FormElement>
                  </div>

                  {(values.trial === 'free' || values.trial === 'paid') && (
                    <div className="flex gap-x-2">
                      <div>
                        <FormElement className="!mb-2">
                          <Label>Trial duration</Label>
                          <div className="flex gap-x-2 items-baseline">
                            <div className="w-20">
                              <TextInput
                                name="trialPeriodDays"
                                id="trialPeriodDays"
                                type="number"
                                placeholder="7"
                              />
                            </div>
                            <div className="text-gray-700">Days</div>
                          </div>

                          <FormError name="trialPeriodDays" />
                        </FormElement>
                      </div>

                      {values.trial === 'paid' && (
                        <div>
                          <FormElement className="!mb-2">
                            <Label>
                              Setup fee
                              <InfoTooltip>
                                Add a one-time setup fee that will be charged immediately.
                              </InfoTooltip>
                            </Label>
                            <div className="flex gap-x-2 items-baseline">
                              <div className="w-20">
                                <TextInput
                                  id="setupFee"
                                  name="setupFee"
                                  type="number"
                                  step="any"
                                  placeholder={'19.99'}
                                />
                              </div>
                              <div className="text-gray-700">{values.currency.toUpperCase()}</div>
                            </div>

                            <FormError name="setupFee">
                              {e => (
                                <FormErrorBase>
                                  {e === 'min'
                                    ? `Minimum ${formatPrice({
                                        amount: getMinimumChargeAmount(values.currency),
                                        currency: values.currency,
                                      })}`
                                    : e}
                                </FormErrorBase>
                              )}
                            </FormError>
                          </FormElement>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="space-y-2">
              <div>
                {/* spacer for space-y-2 glitch https://github.com/tailwindlabs/tailwindcss/issues/2740 */}
              </div>

              {values.type !== PRODUCT_TYPES.PWYW && (
                <>
                  <CheckboxReveal
                    label={
                      <>
                        Discounted from
                        <InfoTooltip>
                          Show strike-through pricing to indicate a discount.
                        </InfoTooltip>
                      </>
                    }
                    initialIsVisible={!!values.discountedFromPrice}
                    onToggle={checked => !checked && setFieldValue('discountedFromPrice', '')}
                  >
                    <div className="w-20 mb-4">
                      <TextInput
                        id="discountedFromPrice"
                        name="discountedFromPrice"
                        type="number"
                        step="any"
                        placeholder={
                          values.price ? (Number(values.price) + 10).toFixed(2) : '29.99'
                        }
                      />
                    </div>

                    <FormError name="discountedFromPrice" />
                  </CheckboxReveal>
                </>
              )}

              {(values.type === PRODUCT_TYPES.ONE_TIME || values.type === PRODUCT_TYPES.PWYW) && (
                <CanUseFeature feature={Features.SETUP_FEE} inset iconSize="small">
                  <CheckboxReveal
                    label={
                      <>
                        Setup fee
                        <InfoTooltip>
                          Add a one-time setup fee that will be charged immediately.
                        </InfoTooltip>
                      </>
                    }
                    initialIsVisible={!!values.setupFee}
                    onToggle={checked => !checked && setFieldValue('setupFee', '')}
                  >
                    <div className="flex gap-x-2 items-baseline mb-4">
                      <div className="w-20">
                        <TextInput
                          id="setupFee"
                          name="setupFee"
                          type="number"
                          step="any"
                          placeholder={'19.99'}
                        />
                      </div>
                      <div className="text-gray-700">{values.currency.toUpperCase()}</div>
                    </div>

                    <FormError name="setupFee">
                      {e => (
                        <FormErrorBase>
                          {e === 'min'
                            ? `Minimum ${formatPrice({
                                amount: getMinimumChargeAmount(values.currency),
                                currency: values.currency,
                              })}`
                            : e}
                        </FormErrorBase>
                      )}
                    </FormError>

                    <FormElement>
                      <Checkbox.Wrapper>
                        <Checkbox
                          name="setupFeeMultipliesWithQuantity"
                          id="setupFeeMultipliesWithQuantity"
                        />
                        <Checkbox.Label htmlFor="setupFeeMultipliesWithQuantity">
                          Multiply by quantity
                          <InfoTooltip>
                            Add a quantity field to multiply the setup fee by the selected quantity
                          </InfoTooltip>
                        </Checkbox.Label>
                      </Checkbox.Wrapper>

                      <FormError name="setupFeeMultipliesWithQuantity" />
                    </FormElement>
                  </CheckboxReveal>
                </CanUseFeature>
              )}

              {(values.type === PRODUCT_TYPES.SUBSCRIPTION ||
                values.type === PRODUCT_TYPES.PAYMENT_PLAN) && (
                <>
                  <CanUseFeature feature={Features.SUBSCRIPTION_START_DATE} inset iconSize="small">
                    <CheckboxReveal
                      label={
                        <>
                          Start date
                          <InfoTooltip>
                            Start the {getPaymentTypeLabel(values.type)} on a specific date and time
                            in the future.
                          </InfoTooltip>
                        </>
                      }
                      initialIsVisible={!!values.startDate}
                      onToggle={checked => !checked && setFieldValue('startDate', '')}
                    >
                      <div className="flex gap-x-2 items-baseline mb-4">
                        <div className="flex wrap w-full items-baseline gap-x-2">
                          <div className="w-48">
                            <TextInput
                              type="datetime-local"
                              name="startDate"
                              id="startDate"
                              placeholder="YYYY-MM-DD"
                              border
                            />
                          </div>
                        </div>
                      </div>

                      <FormError name="startDate" />
                    </CheckboxReveal>
                  </CanUseFeature>

                  {values.billingPeriod !== 'day' && values.billingPeriod !== 'week' && (
                    <CheckboxReveal
                      label={
                        <>
                          Billing anchor
                          <InfoTooltip>
                            The first billing period will be prorated based on the remaining days in
                            the month.
                          </InfoTooltip>
                        </>
                      }
                      initialIsVisible={!!values.billingCycleAnchorConfig.enabled}
                      onToggle={checked => {
                        if (!checked) {
                          setFieldValue('billingCycleAnchorConfig.enabled', false);
                        } else {
                          setFieldValue('billingCycleAnchorConfig.enabled', true);
                        }
                      }}
                    >
                      <div className="mb-4 space-y-2">
                        <div className="w-40">
                          <FormElement className="!mb-2">
                            <Label>Day of month</Label>
                            <Select
                              name="billingCycleAnchorConfig.dayOfMonth"
                              id="billingCycleAnchorConfig.dayOfMonth"
                            >
                              {daysOfMonth.map(day => (
                                <option value={day} key={day}>
                                  {day}
                                </option>
                              ))}
                            </Select>
                          </FormElement>
                        </div>

                        {values.billingPeriod !== 'month' && (
                          <div className="w-full space-y-2">
                            <CheckboxReveal
                              label={
                                <>
                                  Anchor on month
                                  <InfoTooltip>
                                    For multi-month and yearly billing periods, select the month of
                                    the year to anchor the billing cycle on.
                                  </InfoTooltip>
                                </>
                              }
                              initialIsVisible={!!values.billingCycleAnchorConfig.month}
                              onToggle={checked => {
                                if (!checked) {
                                  setFieldValue('billingCycleAnchorConfig.month', null);
                                } else {
                                  setFieldValue('billingCycleAnchorConfig.month', 1);
                                }
                              }}
                            >
                              <div className="w-40">
                                <FormElement className="!mb-0">
                                  <Select
                                    name="billingCycleAnchorConfig.month"
                                    id="billingCycleAnchorConfig.month"
                                  >
                                    {months.map(month => (
                                      <option value={month.name} key={month.name}>
                                        {month.label}
                                      </option>
                                    ))}
                                  </Select>
                                </FormElement>
                              </div>
                            </CheckboxReveal>
                          </div>
                        )}
                      </div>
                    </CheckboxReveal>
                  )}

                  {values.trial !== 'paid' && (
                    <CanUseFeature feature={Features.SETUP_FEE} inset iconSize="small">
                      <CheckboxReveal
                        label={
                          <>
                            Setup fee
                            <InfoTooltip>
                              Add a one-time setup fee that will be charged immediately.
                            </InfoTooltip>
                          </>
                        }
                        initialIsVisible={!!values.setupFee}
                        onToggle={checked => !checked && setFieldValue('setupFee', '')}
                      >
                        <div className="flex gap-x-2 items-baseline mb-4">
                          <div className="w-20">
                            <TextInput
                              id="setupFee"
                              name="setupFee"
                              type="number"
                              step="any"
                              placeholder={'19.99'}
                            />
                          </div>
                          <div className="text-gray-700">{values.currency.toUpperCase()}</div>
                        </div>

                        <FormError name="setupFee">
                          {e => (
                            <FormErrorBase>
                              {e === 'min'
                                ? `Minimum ${formatPrice({
                                    amount: getMinimumChargeAmount(values.currency),
                                    currency: values.currency,
                                  })}`
                                : e}
                            </FormErrorBase>
                          )}
                        </FormError>
                      </CheckboxReveal>
                    </CanUseFeature>
                  )}

                  {checkIsConnectedToStripe(seller) && values.pricingType !== 'multiple' && (
                    <CheckboxReveal
                      label={
                        <>
                          Use Stripe price
                          <InfoTooltip>
                            Select a price from a product in your Stripe account instead of letting
                            us create a new one.
                          </InfoTooltip>
                        </>
                      }
                      initialIsVisible={!!values.planManagedInStripe}
                      onToggle={checked => !checked && setFieldValue('planManagedInStripe', '')}
                    >
                      <div className="mb-4">
                        <SubscriptionPlanSelector planId={values.planId} isNew={isNew} />
                      </div>
                    </CheckboxReveal>
                  )}
                </>
              )}

              {/* {showTaxSettings && (
                  <CheckboxReveal
                    label={
                      <>
                        Override default tax behavior
                        <InfoTooltip>
                          Override the default tax behavior for this product
                        </InfoTooltip>
                      </>
                    }
                    initialIsVisible={values.taxBehavior !== ''}
                    onToggle={checked => {
                      if (checked) {
                        setFieldValue('taxBehavior', 'inclusive');
                      } else {
                        setFieldValue('taxBehavior', '');
                      }
                    }}
                  >
                    <Checkbox.Wrapper>
                      <Checkbox
                        name="taxBehavior"
                        id="taxBehavior"
                        checked={values.taxBehavior === 'inclusive'}
                        onChange={e => {
                          if (e.target.checked) {
                            setFieldValue('taxBehavior', 'inclusive');
                          } else {
                            setFieldValue('taxBehavior', 'exclusive');
                          }
                        }}
                      />

                      <Checkbox.Label htmlFor="taxBehavior">
                        Include tax in price
                        <InfoTooltip>
                          Whether tax is included in the price or added on top
                        </InfoTooltip>
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                  </CheckboxReveal>
                )} */}
            </div>
          </>

          <FormErrorBase>{status && status}</FormErrorBase>

          <FormSubmit>
            <FormSubmit.Button
              appearance="primary"
              size={isNew ? 'full' : 'base'}
              arrowRight={isNew ? true : false}
            >
              {isNew ? 'Continue' : 'Save'}
            </FormSubmit.Button>

            <FormSubmit.State visible={!isNew && dirty} />
          </FormSubmit>
        </Form>
      )}
    </Formik>
  );
};

function TrialField({ setFieldValue }) {
  return (
    <>
      <RadioCard.Group name="trial" columns={3}>
        <RadioCard.Card
          name="trial"
          value=""
          required
          onChange={() => {
            setFieldValue('trialPeriodDays', '');
            setFieldValue('trial', '');
          }}
        >
          <RadioCard.Label>None</RadioCard.Label>
        </RadioCard.Card>

        <RadioCard.Card
          name="trial"
          value="free"
          required
          onChange={() => {
            setFieldValue('trial', 'free');
          }}
        >
          <RadioCard.Label>Free</RadioCard.Label>
        </RadioCard.Card>

        <RadioCard.Card
          name="trial"
          value="paid"
          required
          onChange={() => {
            setFieldValue('trial', 'paid');
          }}
        >
          <RadioCard.Label>Paid</RadioCard.Label>
        </RadioCard.Card>
      </RadioCard.Group>
    </>
  );
}

function BillingModelField() {
  const { plan } = useAuth();
  return (
    <RadioCard.Group name="type">
      <RadioCard.Card name="type" value={PRODUCT_TYPES.ONE_TIME} required>
        <RadioCard.Label>One-time</RadioCard.Label>
      </RadioCard.Card>

      <RadioCard.Card name="type" value={PRODUCT_TYPES.SUBSCRIPTION} required>
        <RadioCard.Label>Subscription</RadioCard.Label>
      </RadioCard.Card>

      <CanUseFeature feature={Features.PAYMENT_PLAN} inset reduceOpacity={false} iconSize="small">
        <RadioCard.Card
          name="type"
          value={PRODUCT_TYPES.PAYMENT_PLAN}
          required
          disabled={!canUseFeature(Features.PAYMENT_PLAN, plan.handle)}
        >
          <RadioCard.Label>Payment plan</RadioCard.Label>
        </RadioCard.Card>
      </CanUseFeature>

      <CanUseFeature
        feature={Features.PAY_WHAT_YOU_WANT_PRICING}
        inset
        reduceOpacity={false}
        iconSize="small"
      >
        <RadioCard.Card
          name="type"
          value={PRODUCT_TYPES.PWYW}
          required
          disabled={!canUseFeature(Features.PAY_WHAT_YOU_WANT_PRICING, plan.handle)}
        >
          <RadioCard.Label>Pay what you want</RadioCard.Label>
        </RadioCard.Card>
      </CanUseFeature>
    </RadioCard.Group>
  );
}

function PricingTypeField() {
  return (
    <RadioCard.Group name="pricingType">
      <RadioCard.Card name="pricingType" value="single" required>
        Single price
      </RadioCard.Card>
      <RadioCard.Card name="pricingType" value="multiple" required>
        Multiple prices
      </RadioCard.Card>
    </RadioCard.Group>
  );
}

export default ProductDetailsForm;
